/**
 * DiscontinueTreatmentModal - Modal for discontinuing/stopping a treatment cycle
 */

import React, { useState, useCallback } from "react";
import { View, TextInput } from "react-native";
import { Text } from "@louise/ui/Text/Text";
import { Button } from "@louise/ui/Button/Button";
import {
  DynamicModal,
  DynamicModalHeader,
  DynamicModalContent,
  DynamicModalFooter,
} from "@louise/ui/DynamicModal/DynamicModal";
import { Pause } from "lucide-react-native";

interface DiscontinueTreatmentModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (notes: string) => void | Promise<void>;
  isLoading?: boolean;
}

export function DiscontinueTreatmentModal({
  open,
  onClose,
  onConfirm,
  isLoading = false,
}: DiscontinueTreatmentModalProps) {
  const [notes, setNotes] = useState("");

  // Reset form when modal closes
  const handleClose = useCallback(() => {
    setNotes("");
    onClose();
  }, [onClose]);

  // Handle confirm
  const handleConfirm = useCallback(async () => {
    await onConfirm(notes.trim());
    setNotes("");
  }, [notes, onConfirm]);

  return (
    <DynamicModal
      open={open}
      onClose={handleClose}
      backgroundStyle={{ backgroundColor: "#0F172A" }}
      snapPoints={["50%"]}
    >
      <DynamicModalContent className="gap-5 px-6 pt-6">
        {/* Header with icon and title */}
        <View className="flex-row items-center gap-3">
          {/* Pause icon in circle */}
          <View
            style={{
              width: 40,
              height: 40,
              borderRadius: 20,
              borderWidth: 2,
              borderColor: "#F43F5E",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Pause size={18} color="#F43F5E" strokeWidth={2.5} />
          </View>
          <Text className="text-xl font-semibold text-white">
            Did something change?
          </Text>
        </View>

        {/* Description */}
        <Text className="text-[0.9rem] leading-relaxed text-slate-400">
          This will stop the current cycle. All your treatment information,
          progress, and records will remain accessible from the treatment
          summary page.
        </Text>

        {/* Notes section */}
        <View>
          <Text className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
            Notes
          </Text>
          <TextInput
            value={notes}
            onChangeText={setNotes}
            placeholder="If you wish, you can share a few words about why you're choosing to discontinue this treatment."
            placeholderTextColor="#64748b"
            multiline
            numberOfLines={4}
            className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white"
            style={{ minHeight: 100, textAlignVertical: "top" }}
          />
        </View>
      </DynamicModalContent>

      <DynamicModalFooter className="px-6 pb-6">
        <Button
          onPress={handleConfirm}
          disabled={isLoading}
          className="w-full rounded-xl bg-[#F43F5E] py-4"
        >
          <Text className="text-center text-base font-semibold text-white">
            {isLoading ? "Discontinuing..." : "Discontinue treatment"}
          </Text>
        </Button>
      </DynamicModalFooter>
    </DynamicModal>
  );
}
